'use client';

import { useState, useMemo, useEffect } from 'react';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { MiniDetailViewDataDocument } from '@packages/gql/generated/shopping/MiniDetailViewDataDocument';
import { type ProductPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPriceFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { useQuery } from 'urql';
import { useIntl } from 'react-intl';
import type { DialogProps } from '@packages/shared/src/ui/Dialog/Dialog';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogFooter } from '@packages/shared/src/ui/Dialog/DialogFooter/DialogFooter';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import {
  AddToBasketButtonTailwind,
  type AddToBasketButtonTailwindProps,
} from '../AddToBasketButton/AddToBasketButtonTailwind.client';
import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader.client';
import { getProductAndFallbackId } from '../helpers/getProductAndFallbackId/getProductAndFallbackId';
import { EditInWishlistButton } from '../EditInWishlistButton/EditInWishlistButton';
import { MiniDVSkeleton } from './MiniDVSkeleton/MiniDVSkeleton';
import { MiniDV } from './MiniDV/MiniDV';
import { miniDVMessages } from './messages';

/* GraphQL */ `
  query MiniDetailViewData(
    $productId: String!
    $fallbackId: String!
    $locale: String!
    $lang: String!
    $skipAplus: Boolean! = false
  ) {
    product(id: $productId, fallbackId: $fallbackId, locale: $locale) {
      ...EditInWishlistFragment
      # there is no AddToBasketFragment, so the fields are inlined here. the other on Product fragments should also be resolved!
      akl
      aplus(language: $lang) @skip(if: $skipAplus) {
        ...GetGlycerinServicesFragment
      }
      availability {
        hasExpressService
        isOrderable
        limitedStock
        message
        state
      }
      brand {
        code
        image
        name
        url
      }
      defaultCategory
      dimensions {
        # FRAGMENT is okay here because RawProduct query is using the same fragment
        ...DimensionFragment
      }
      # !!! need to be resolved
      ...EfficiencyFlagsAndDataSheetsDownloadCardFragment
      itemOptionCommKey
      longDescription
      marketcode
      media {
        type
        url
      }
      name
      orderNumber
      powerEfficiencyFlags {
        color
        hasOldLabel
        level
        link
        text
      }
      powerSupply {
        ...ProductPowerSupplyFragment
      }
      price {
        ...ProductPriceFragment
      }
      promotion
      sku
      tireEfficiency {
        link
        flags {
          level
          type
        }
      }
    }
  }
`;

export type MiniDVLayerProps = {
  /**
   * product sku
   */
  sku: string;
  /**
   * product akl
   */
  akl: string;
  /**
   * it needs to update current lineItem in the basket.
   * without lineItemId it will add new product in the the basket
   */
  lineItemId?: string;
  /**
   * quantity of the lineItem
   */
  quantity: number;
  /**
   * mode for handling the data
   * @default 'ATB' - Add to Basket
   * 'EIW' - Edit in Wishlist
   */
  mode?: 'ATB' | 'EIW';
  open: DialogProps['isOpen'];
} & Pick<DialogProps, 'onClose'> &
  Pick<AddToBasketButtonTailwindProps, 'onClick'>;

export const MiniDVLayer = ({
  sku,
  akl,
  lineItemId,
  quantity,
  open,
  onClose,
  onClick: onButtonClick,
  mode = 'ATB',
}: MiniDVLayerProps) => {
  const [selectedVariation, setSelectedVariation] = useState<string | null>(sku);
  const { formatMessage } = useIntl();
  const dispatchGlycerin = useTracking();
  const { locale, language } = useI18n();
  const {
    product: {
      aplus: { enabled: aplusEnabled },
    },
  } = useConfig();

  const [{ data, fetching }] = useQuery({
    query: MiniDetailViewDataDocument,
    variables: {
      ...getProductAndFallbackId(akl, selectedVariation!),
      locale,
      lang: language,
      skipAplus: !aplusEnabled,
    },
    pause: !open,
    context: useMemo(() => ({ suspense: false }), []),
  });

  useEffect(() => {
    // Only track if the dialog is open
    if (open) {
      dispatchDisplayOverlayTracking({
        eventDispatcher: dispatchGlycerin,
        displayTrackingProps: { category: 'Mini-DV', label: 'Produktkonfiguration' },
      });
    }
  }, [dispatchGlycerin, open]);

  const productData = data?.product;

  if (fetching && !productData) {
    return (
      <Dialog isOpen={open} onClose={onClose}>
        <DialogHeader onClose={onClose}>{formatMessage(miniDVMessages.title)}</DialogHeader>
        <MiniDVSkeleton />
      </Dialog>
    );
  }

  if (!productData) {
    return null;
  }

  const {
    orderNumber,
    promotion,
    availability,
    name,
    longDescription,
    defaultCategory,
    brand,
    dimensions,
    marketcode,
    media,
    akl: selectedAkl,
    sku: selectedSku,
  } = productData;

  const price = unmask<typeof ProductPriceFragmentFragmentDoc>(productData.price);

  return (
    <Dialog isOpen={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>{formatMessage(miniDVMessages.title)}</DialogHeader>
      {productData && (
        <>
          <MiniDV
            data={productData}
            setSelectedVariation={setSelectedVariation}
            quantity={quantity}
            hideCounter={mode === 'EIW'}
          />
          <DialogFooter>
            <div className="flex flex-col justify-end">
              {mode === 'EIW' && (
                <EditInWishlistButton data={productData} prevSku={sku} onClose={onClose} />
              )}
              {mode === 'ATB' && (
                <AddToBasketButtonTailwind
                  trackingProductData={{
                    akl: selectedAkl,
                    availability,
                    sku: selectedSku,
                    brand,
                    defaultCategory,
                    orderNumber,
                    dimensions,
                    longDescription,
                    marketcode,
                    media,
                    name,
                    promotion,
                    price,
                  }}
                  size="large"
                  lineItemId={lineItemId}
                  onClose={onClose}
                  onClick={onButtonClick}
                />
              )}
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
